import { cn } from "@/lib/utils";

const TONES = {
  // On light surfaces – faithful to the original Proxima Hydrotech logo
  brand: { ring: "#0860B0", fill: "#90D8F8", inner: "#FFFFFF", bracket: "#6FBDE8" },
  // On the dark navy rail / dark photo overlays
  onDark: { ring: "#90D8F8", fill: "#0860B0", inner: "rgba(255,255,255,0.92)", bracket: "rgba(144,216,248,0.75)" },
  // Monochrome (currentColor) for subtle decorative use
  mono: { ring: "currentColor", fill: "transparent", inner: "currentColor", bracket: "currentColor" },
};

/** The Proxima lens / viewfinder mark (the "O" in PROXIMA). */
export const LensMark = ({ className, tone = "brand", title = "Proxima", ...props }) => {
  const c = TONES[tone] || TONES.brand;
  return (
    <svg viewBox="0 0 100 100" className={cn("shrink-0", className)} role="img" aria-label={title} {...props}>
      <g fill="none" stroke={c.bracket} strokeWidth="7" strokeLinecap="square">
        <path d="M5 27V5h22" />
        <path d="M73 5h22v22" />
        <path d="M95 73v22H73" />
        <path d="M27 95H5V73" />
      </g>
      <circle cx="50" cy="50" r="30" fill={c.fill} stroke={c.ring} strokeWidth="12" />
      <circle cx="50" cy="50" r="14" fill="none" stroke={c.inner} strokeWidth="5" />
    </svg>
  );
};

/**
 * Proxima wordmark: PR[lens]XIMA with optional tagline ("Hydrotech" or "Portal").
 * Sized via font-size on the wrapper (em based), so pass text-* classes in className.
 */
export const ProximaLogo = ({ className, tone = "brand", tagline = "Hydrotech", ...props }) => {
  const dark = tone === "onDark";
  return (
    <div className={cn("inline-flex flex-col leading-none select-none", className)} data-testid="app-logo" {...props}>
      <div className={cn("flex items-center font-brand font-medium uppercase tracking-[0.2em] whitespace-nowrap", dark ? "text-white" : "text-brand-blue")}>
        <span>PR</span>
        <LensMark tone={tone} className="mx-[0.05em] h-[1.12em] w-[1.12em] translate-y-[-0.03em]" />
        <span>XIMA</span>
      </div>
      {tagline ? (
        <div className={cn("mt-[0.34em] font-brand font-medium uppercase tracking-[0.4em] text-[0.5em] whitespace-nowrap pl-[0.1em]", dark ? "text-brand-cyan/80" : "text-brand-blue/75")}>{tagline}</div>
      ) : null}
    </div>
  );
};

/** Four thin corner brackets – the lens motif used as a decorative frame. */
export const LensCorners = ({ className, size = 14, inset = 10, color = "hsl(var(--primary) / 0.35)" }) => {
  const s = `${size}px`;
  const i = `${inset}px`;
  const base = { position: "absolute", width: s, height: s, borderColor: color, borderStyle: "solid", borderWidth: 0 };
  return (
    <div className={cn("pointer-events-none absolute inset-0", className)} aria-hidden="true">
      <span style={{ ...base, top: i, left: i, borderTopWidth: 1.5, borderLeftWidth: 1.5 }} />
      <span style={{ ...base, top: i, right: i, borderTopWidth: 1.5, borderRightWidth: 1.5 }} />
      <span style={{ ...base, bottom: i, right: i, borderBottomWidth: 1.5, borderRightWidth: 1.5 }} />
      <span style={{ ...base, bottom: i, left: i, borderBottomWidth: 1.5, borderLeftWidth: 1.5 }} />
    </div>
  );
};
